import { useMemo } from "react";
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { cn } from "~/lib/utils";
import { useTablePreview } from "./hooks/useTablePreview";
import { TablePreviewModalProps, TablePreviewVariant } from "./types";

const SKELETON_COLUMN_COUNT = 4;
const SKELETON_ROW_COUNT = 5;

function SkeletonRows({ columnCount }: { columnCount: number }) {
  return (
    <>
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIdx) => (
        <TableRow key={rowIdx}>
          {Array.from({ length: columnCount }).map((_, colIdx) => (
            <TableCell key={colIdx}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

export function TablePreviewModal({
  open,
  onClose,
  tableFqn,
  tableName,
  variant = TablePreviewVariant.COMPACT,
}: TablePreviewModalProps) {
  const {
    data,
    isLoading,
    error,
    pageIndex,
    pageSize,
    setPageIndex,
    sortingState,
    setSortingState,
  } = useTablePreview(open ? tableFqn : "", variant);

  const columnCount = data?.metadata.table.columns.length ?? SKELETON_COLUMN_COUNT;
  const totalCount = data?.metadata.totalCount ?? 0;
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : 1;
  const schemaLocation = data?.metadata.table.schemaname;

  const columns: ColumnDef<Record<string, unknown>>[] = useMemo(
    () =>
      (data?.metadata.table.columns ?? []).map((col) => ({
        id: col.name,
        accessorFn: (row: Record<string, unknown>) => row[col.name],
        header: col.name,
        cell: ({ getValue }) => {
          const v = getValue();
          if (v === null || v === undefined) {
            return <span className="text-muted-foreground italic">null</span>;
          }
          if (typeof v === "number") {
            return Number.isInteger(v) ? v : v.toFixed(4);
          }
          return String(v);
        },
      })),
    [data?.metadata.table.columns]
  );

  const tableInstance = useReactTable({
    data: data?.items ?? [],
    columns,
    manualPagination: true,
    manualSorting: true,
    rowCount: totalCount,
    state: { sorting: sortingState },
    onSortingChange: setSortingState,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="mx-4 flex max-h-[90vh] flex-col gap-0 sm:max-w-5xl">
        <DialogHeader className="shrink-0 px-4 pb-3 pt-4">
          <DialogTitle>{tableName}</DialogTitle>
          {schemaLocation && <DialogDescription>{schemaLocation}</DialogDescription>}
        </DialogHeader>

        {/* Scrollable table area */}
        <div className="min-h-0 flex-1 overflow-auto px-4">
          {error ? (
            <div className="flex h-32 flex-col items-center justify-center gap-2 text-muted-foreground">
              <AlertCircle className="size-5" />
              <p className="text-sm">
                Failed to load preview. You may not have access to this table.
              </p>
            </div>
          ) : (
            <Table tableContainerProps={{ className: "border rounded-md" }}>
              <TableHeader>
                {isLoading ? (
                  <TableRow>
                    {Array.from({ length: columnCount }).map((_, i) => (
                      <TableHead key={i}>
                        <Skeleton className="h-4 w-20" />
                      </TableHead>
                    ))}
                  </TableRow>
                ) : (
                  tableInstance.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id} className="bg-muted/50">
                      {headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          className={cn(
                            "text-xs",
                            header.column.getCanSort() && "cursor-pointer select-none"
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {header.isPlaceholder ? null : (
                            <span className="flex items-center gap-1">
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {header.column.getIsSorted() === "asc" && (
                                <ArrowUp className="size-3" />
                              )}
                              {header.column.getIsSorted() === "desc" && (
                                <ArrowDown className="size-3" />
                              )}
                              {!header.column.getIsSorted() && header.column.getCanSort() && (
                                <ArrowUpDown className="size-3 opacity-40" />
                              )}
                            </span>
                          )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <SkeletonRows columnCount={columnCount} />
                ) : totalCount === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columnCount}
                      className="h-16 text-center text-muted-foreground"
                    >
                      This table has no rows.
                    </TableCell>
                  </TableRow>
                ) : (
                  tableInstance.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="text-xs">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Pagination footer */}
        {!error && (
          <div className="flex shrink-0 items-center justify-between border-t px-4 py-3">
            <span className="text-xs text-muted-foreground">
              {isLoading ? (
                <Skeleton className="h-4 w-24" />
              ) : (
                `${totalCount.toLocaleString()} total rows`
              )}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageIndex((p) => p - 1)}
                disabled={pageIndex === 0 || isLoading}
                aria-label="Previous page"
                className="h-7 has-[>svg]:px-1.5"
              >
                <ChevronLeft className="size-4" />
              </Button>
              <span className="px-2 text-xs text-muted-foreground">
                Page {pageIndex + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageIndex((p) => p + 1)}
                disabled={!data?.metadata.hasMore || isLoading}
                aria-label="Next page"
                className="h-7 has-[>svg]:px-1.5"
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
